import type { MotionValue } from 'motion/react'
import type { ElementType, FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'

export type ScrubSegment = {
    text: string
    emphasis?: boolean
}

interface WordProps {
    word: string
    progress: MotionValue<number>
    range: [number, number]
    emphasis?: boolean
    emphasisClassName?: string
    still?: boolean
}

const Word: FC<WordProps> = ({ word, progress, range, emphasis, emphasisClassName, still }): ReactNode => {
    const opacity = useTransform(progress, range, still ? [1, 1] : [0.14, 1])

    return (
        <>
            <motion.span
                className={cn('inline-block', emphasis && emphasisClassName)}
                style={{ opacity }}
            >
                {word}
            </motion.span>
            {' '}
        </>
    )
}

interface ScrubWordsProps {
    segments: ScrubSegment[]
    as?: ElementType
    className?: string
    emphasisClassName?: string
    offset?: [string, string]
}

// Reading light: each word lights up as the passage travels through the viewport.
export const ScrubWords: FC<ScrubWordsProps> = ({
    segments,
    as: Tag = 'p',
    className,
    emphasisClassName = 'font-serif italic text-tone-accent',
    offset = ['start 0.85', 'end 0.45']
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const reduce = useReducedMotion()
    const { scrollYProgress } = useScroll({ target: ref, offset: offset as any })

    const words = segments.flatMap(segment =>
        segment.text.split(/\s+/).filter(Boolean).map(word => ({ word, emphasis: Boolean(segment.emphasis) }))
    )

    return (
        <Tag ref={ref} className={className}>
            {words.map((item, index) => {
                const start = index / words.length
                const end = Math.min(1, start + 1.6 / words.length + 0.04)

                return (
                    <Word
                        key={`${item.word}-${index}`}
                        word={item.word}
                        emphasis={item.emphasis}
                        emphasisClassName={emphasisClassName}
                        progress={scrollYProgress}
                        range={[start, end]}
                        still={Boolean(reduce)}
                    />
                )
            })}
        </Tag>
    )
}
